export type EventIntakeCategory = 'corporate_event' | 'private_party' | 'full_club_buyout';

export type EventIntakeFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'date'
  | 'time'
  | 'number'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'textarea';

export interface EventIntakeField {
  name: string;
  label: string;
  type: EventIntakeFieldType;
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface EventIntakeSection {
  title: string;
  fields: EventIntakeField[];
}

export interface EventIntakeConfig {
  id: EventIntakeCategory;
  eyebrow: string;
  title: string;
  cardTitle: string;
  summary: string;
  bestFor: string;
  capacity: string;
  ctaLabel: string;
  submitLabel: string;
  sections: EventIntakeSection[];
}

const yesNo = ['Yes', 'No'];
const courts = ['Padel', 'Pickleball', 'Both'];

export const EVENT_INTAKE_CONFIGS: EventIntakeConfig[] = [
  {
    id: 'corporate_event',
    eyebrow: 'Teams and clients',
    title: 'Corporate Event Intake',
    cardTitle: 'Corporate Events',
    summary:
      'Team offsites, client events, recruiting moments, and holiday parties with court programming, food, beverage, and optional branding.',
    bestFor: 'Offsites, client entertaining, team building, holiday parties.',
    capacity: '8 to 100 guests',
    ctaLabel: 'Start Corporate Intake',
    submitLabel: 'Send Corporate Inquiry',
    sections: [
      {
        title: 'Company Information',
        fields: [
          { name: 'company_name', label: 'Company Name', type: 'text', required: true },
          { name: 'contact_person', label: 'Contact Person', type: 'text', required: true },
          { name: 'phone', label: 'Phone', type: 'tel', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
        ],
      },
      {
        title: 'Event Details',
        fields: [
          { name: 'event_type', label: 'Event Type', type: 'text' },
          { name: 'event_date', label: 'Event Date', type: 'date' },
          { name: 'event_time', label: 'Time', type: 'text', placeholder: 'e.g. 2 PM to 5 PM' },
          { name: 'number_of_attendees', label: 'Number of Attendees', type: 'number' },
        ],
      },
      {
        title: 'Facility and Setup',
        fields: [
          { name: 'area_rental_needed', label: 'Area Rental Needed?', type: 'radio', options: yesNo },
          { name: 'area_rental_details', label: 'If yes, specify', type: 'text' },
          { name: 'meeting_space_needed', label: 'Meeting Space Needed?', type: 'radio', options: yesNo },
          { name: 'seating_setup', label: 'Seating Setup', type: 'select', options: ['Theater', 'Classroom', 'Casual'] },
        ],
      },
      {
        title: 'Activities',
        fields: [
          { name: 'court_access_needed', label: 'Court Access Needed?', type: 'radio', options: yesNo },
          { name: 'court_type', label: 'Type', type: 'select', options: courts },
          { name: 'number_of_courts', label: 'Number of Courts', type: 'number' },
          { name: 'team_building_activities', label: 'Team Building Activities?', type: 'radio', options: yesNo },
          { name: 'coaching_instruction', label: 'Coaching / Instruction?', type: 'radio', options: yesNo },
        ],
      },
      {
        title: 'Food, Beverage, and Extras',
        fields: [
          { name: 'catering_needed', label: 'Catering Needed?', type: 'radio', options: yesNo },
          { name: 'meal_type', label: 'Meal Type', type: 'select', options: ['Breakfast', 'Lunch', 'Dinner', 'Snacks'] },
          { name: 'beverage_service', label: 'Beverage Service', type: 'radio', options: yesNo },
          { name: 'company_branding_signage', label: 'Company Branding / Signage?', type: 'radio', options: yesNo },
          { name: 'giveaways_swag', label: 'Giveaways / Swag?', type: 'radio', options: yesNo },
          { name: 'special_requirements_notes', label: 'Special Requirements / Notes', type: 'textarea' },
        ],
      },
    ],
  },
  {
    id: 'private_party',
    eyebrow: 'Celebrations',
    title: 'Private Party Intake',
    cardTitle: 'Private Parties',
    summary:
      'Birthdays, bachelor and bachelorette parties, graduations, and family celebrations with beginner-friendly play and lounge time.',
    bestFor: 'Birthdays, milestones, family gatherings, and social celebrations.',
    capacity: '8 to 50 guests',
    ctaLabel: 'Start Private Party Intake',
    submitLabel: 'Send Private Party Inquiry',
    sections: [
      {
        title: 'Client Information',
        fields: [
          { name: 'client_name', label: 'Name', type: 'text', required: true },
          { name: 'phone', label: 'Phone', type: 'tel', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
        ],
      },
      {
        title: 'Event Details',
        fields: [
          { name: 'honoree_name', label: 'Honoree / Guest of Honor Name', type: 'text' },
          { name: 'occasion', label: 'Occasion', type: 'text', placeholder: 'Birthday, graduation, bachelor party, etc.' },
          { name: 'age_optional', label: 'Age (optional)', type: 'number' },
          { name: 'event_date', label: 'Event Date', type: 'date' },
          { name: 'event_time', label: 'Event Time', type: 'text', placeholder: 'e.g. afternoon, 6 PM' },
          { name: 'number_of_guests', label: 'Number of Guests', type: 'number' },
        ],
      },
      {
        title: 'Facility and Activity Preferences',
        fields: [
          { name: 'area_rental_needed', label: 'Area Rental Needed?', type: 'radio', options: yesNo },
          { name: 'area_rental_details', label: 'If yes, specify area', type: 'text' },
          { name: 'courts_needed', label: 'Courts Needed', type: 'select', options: courts },
          { name: 'number_of_courts', label: 'Number of Courts', type: 'number' },
          { name: 'equipment_rental_needed', label: 'Equipment Rental Needed?', type: 'radio', options: yesNo },
        ],
      },
      {
        title: 'Food, Beverage, and Extras',
        fields: [
          { name: 'cake', label: 'Will there be a cake?', type: 'radio', options: yesNo },
          { name: 'cake_provided_by', label: 'If yes, provided by', type: 'select', options: ['Client', 'Club'] },
          { name: 'food_service_required', label: 'Food Service Required?', type: 'radio', options: yesNo },
          { name: 'beverage_package', label: 'Beverage Package', type: 'radio', options: yesNo },
          { name: 'decorations_needed', label: 'Decorations Needed?', type: 'radio', options: yesNo },
          { name: 'music_dj', label: 'Music / DJ?', type: 'radio', options: yesNo },
          { name: 'instructor_coach_needed', label: 'Instructor / Coach Needed?', type: 'radio', options: yesNo },
          { name: 'special_notes_requests', label: 'Special Notes / Requests', type: 'textarea' },
        ],
      },
    ],
  },
  {
    id: 'full_club_buyout',
    eyebrow: 'Exclusive access',
    title: 'Full Club Buyout Intake',
    cardTitle: 'Full Club Buyouts',
    summary:
      'Reserve the full club for larger private, corporate, or branded events with all courts, lounge, bar, and support staff available.',
    bestFor: 'Large celebrations, product launches, client events, and premium venue takeovers.',
    capacity: '50 to 250 guests',
    ctaLabel: 'Start Buyout Intake',
    submitLabel: 'Send Buyout Inquiry',
    sections: [
      {
        title: 'Client / Organization Information',
        fields: [
          { name: 'company_group_name', label: 'Company / Group Name', type: 'text', required: true },
          { name: 'contact_person', label: 'Contact Person', type: 'text', required: true },
          { name: 'phone', label: 'Phone', type: 'tel', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
        ],
      },
      {
        title: 'Event Details',
        fields: [
          { name: 'event_date', label: 'Event Date', type: 'date', required: true },
          { name: 'start_time', label: 'Start Time', type: 'time' },
          { name: 'end_time', label: 'End Time', type: 'time' },
          { name: 'estimated_attendance', label: 'Estimated Attendance', type: 'number', required: true },
        ],
      },
      {
        title: 'Facility Requirements',
        fields: [
          { name: 'full_club_buyout_confirmed', label: 'Full Club Buyout Confirmed?', type: 'radio', options: yesNo },
          { name: 'indoor_areas_needed', label: 'Indoor Areas Needed?', type: 'radio', options: yesNo },
          { name: 'outdoor_areas_needed', label: 'Outdoor Areas Needed?', type: 'radio', options: yesNo },
          { name: 'specific_areas_requested', label: 'Specific Areas Requested', type: 'textarea' },
        ],
      },
      {
        title: 'Court Usage',
        fields: [
          { name: 'padel_courts', label: 'Padel Courts', type: 'number' },
          { name: 'pickleball_courts', label: 'Pickleball Courts', type: 'number' },
          { name: 'activity_format', label: 'Open Play or Structured Activities?', type: 'text' },
        ],
      },
      {
        title: 'Food, Beverage, Staffing, and Services',
        fields: [
          { name: 'catering_required', label: 'Catering Required?', type: 'radio', options: yesNo },
          { name: 'catering_type', label: 'Type', type: 'select', options: ['Buffet', 'Plated', 'Light Snacks'] },
          { name: 'bar_service_needed', label: 'Bar Service Needed?', type: 'radio', options: yesNo },
          { name: 'event_staff_required', label: 'Event Staff Required?', type: 'radio', options: yesNo },
          { name: 'coaches_instructors', label: 'Coaches / Instructors?', type: 'radio', options: yesNo },
          { name: 'av_sound_system_needed', label: 'AV / Sound System Needed?', type: 'radio', options: yesNo },
          { name: 'additional_requests', label: 'Additional Requests', type: 'textarea' },
        ],
      },
    ],
  },
];

export const ALCOHOL_POLICY =
  'No outside alcoholic beverages are permitted on club premises. All alcoholic beverages must be purchased directly through the club.';

export function getEventIntakeConfig(category: EventIntakeCategory) {
  return EVENT_INTAKE_CONFIGS.find((config) => config.id === category) || EVENT_INTAKE_CONFIGS[0];
}
